import { PhishingSimulation, PhishingSimulationVector } from '@/types/phishingSimulations'
import { Box, styled, Typography } from '@mui/material'
import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import 'react-responsive-carousel/lib/styles/carousel.min.css'
import { useNavigate } from 'react-router-dom'

import macFrameImage from '@/assets/images/preview-mac-frame.png'
import { DeviceType } from '@/common/hooks/useDeviceDetection'
import isLanguageRTL from '@/common/utils/isLanguageRTL'
import Microtraining, { buildMicrotraining } from '@/pages/Microtraining'
import DevicePreview from '@/pages/Microtraining/components/DevicePreview'
import { theme } from '@/theme/theme'

type CampaignSimulationPreviewParams = { simulationData: PhishingSimulation }

const CampaignSimulationPreview: FC<CampaignSimulationPreviewParams> = ({ simulationData }) => {
  const attackVector = simulationData?.vectors[0]
  const data = buildMicrotraining(simulationData)
  const [onHover, setOnHover] = useState(1)
  const navigateTo = useNavigate()
  const { t } = useTranslation()

  return (
    <Box display="flex" flexDirection="column" alignItems="center">
      <Typography fontWeight={theme.typography.fontWeightSemiBold}>{simulationData.name}</Typography>
      <Box display="flex" justifyContent="space-around" gap={5} padding={theme.spacing(3, 0, 4)}>
        <StyledScreenBox
          index={0}
          onMouseEnter={() => {
            setOnHover(0)
          }}
          onMouseLeave={() => {
            setOnHover(1)
          }}
          attackVector={attackVector}
          onHover={onHover}>
          <StyledMessageBox attackVector={attackVector}>
            <DevicePreview
              deviceType={DeviceType.Desktop}
              attackVector={attackVector as PhishingSimulationVector}
              data={data}
              isRtl={isLanguageRTL(data?.message.language)}
            />
          </StyledMessageBox>
        </StyledScreenBox>

        <StyledScreenBox onHover={onHover} index={1}>
          <StyledLadingPageBox>
            <iframe
              srcDoc={simulationData.html_contents.landing_page}
              style={{ border: 'none', width: '1920px', height: '1080px' }}
            />
          </StyledLadingPageBox>
        </StyledScreenBox>

        <StyledScreenBox
          onMouseEnter={() => {
            setOnHover(2)
          }}
          onMouseLeave={() => {
            setOnHover(1)
          }}
          onHover={onHover}
          index={2}>
          <StyledMicroTrainingBox>
            <Microtraining isPreview={true} directSimulationId={simulationData.id} />
          </StyledMicroTrainingBox>
        </StyledScreenBox>
      </Box>

      <StyledLinkTypography onClick={() => navigateTo(`/content-library/simulations/${simulationData.id}`)}>
        {t('campaignDashboard.campaignPreview.previewSimulation')}
      </StyledLinkTypography>
    </Box>
  )
}

const StyledScreenBox = styled(Box)(
  ({ attackVector, onHover, index }: { attackVector?: string; onHover: number; index: number }) => ({
    backgroundImage: attackVector === PhishingSimulationVector.SMS ? '' : `url(${macFrameImage})`,
    backgroundSize: 'contain',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    minWidth: '300px',
    minHeight: '200px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    transform:
      onHover === index
        ? attackVector === PhishingSimulationVector.SMS
          ? 'scale(1.6)'
          : 'scale(1.3)'
        : attackVector === PhishingSimulationVector.SMS
        ? 'scale(1)'
        : 'scale(0.8)',
    transition: 'transform 0.2s ease-in-out',
    cursor: 'pointer',
  })
)

const StyledMessageBox = styled(Box)(({ attackVector }: { attackVector?: string }) => ({
  width: '1920px',
  height: '1080px',
  position: 'absolute',
  paddingTop: attackVector === PhishingSimulationVector.SMS ? '230px' : '175px',
  pointerEvents: 'none',
  transform: attackVector === PhishingSimulationVector.SMS ? 'scale(0.25)' : 'scale(0.17)',
}))

const StyledLadingPageBox = styled(Box)(() => ({
  transform: 'scale(0.116, 0.117)',
  pointerEvents: 'none',
  position: 'absolute',
  overflow: 'hidden',
  margin: '-4px 0 0 -3px',
}))

const StyledMicroTrainingBox = styled(Box)(() => ({
  transform: 'scale(0.15)',
  pointerEvents: 'none',
  position: 'absolute',
  margin: '-5px 0 0 -5px',
  overflow: 'hidden',
  height: '800px',
}))

const StyledLinkTypography = styled(Typography)(() => ({
  color: theme.palette.blue[900],
  cursor: 'pointer',
  textDecoration: 'underline',
}))

export default CampaignSimulationPreview
